import { setTextSafe } from '../core/utils.js';
import { trackEvent } from '../core/analytics.js';
import { soundEngine } from '../core/audio-engine.js';
import { Mp3Encoder } from '@breezystack/lamejs';
import { formatBytes, showFileAdvisory } from './extra-common.js';

const dropzone = document.getElementById('trimmer-dropzone');
const dropzoneDefault = document.getElementById('trimmer-dropzone-default');
const dropzoneLoading = document.getElementById('trimmer-dropzone-loading');
const dropzoneError = document.getElementById('trimmer-dropzone-error');
const loadingFilename = document.getElementById('trimmer-loading-filename');
const progressBar = document.getElementById('trimmer-progress-bar');
const loadingStep = document.getElementById('trimmer-loading-step');
const loadingPct = document.getElementById('trimmer-loading-pct');
const errorMsg = document.getElementById('trimmer-error-msg');
const retryBtn = document.getElementById('trimmer-retry-btn');
const changeFileBtn = document.getElementById('audio-change-file-btn');

const fileInput = document.getElementById('trimmer-file-input');
const controls = document.getElementById('trimmer-controls');
const totalDurationEl = document.getElementById('audio-total-duration');
const selectedDurationEl = document.getElementById('audio-selected-duration');
const sampleRateEl = document.getElementById('audio-sample-rate');
const currentTimeEl = document.getElementById('audio-current-time');
const waveformCanvas = document.getElementById('audio-waveform-canvas');
const canvasWrap = document.getElementById('audio-canvas-wrap');
const selectionBox = document.getElementById('audio-selection-box');
const markerStart = document.getElementById('audio-marker-start');
const markerEnd = document.getElementById('audio-marker-end');
const markerStartVal = document.getElementById('marker-start-val');
const markerEndVal = document.getElementById('marker-end-val');
const handleL = document.getElementById('audio-handle-l');
const handleR = document.getElementById('audio-handle-r');
const playheadEl = document.getElementById('audio-playhead');
const playheadBadge = document.getElementById('audio-playhead-badge');
const playheadTimeEl = document.getElementById('audio-playhead-time');
const startTimeInput = document.getElementById('audio-start-time');
const endTimeInput = document.getElementById('audio-end-time');
const exportFormatSelect = document.getElementById('audio-export-format');
const bitrateLabel = document.getElementById('audio-bitrate-label');
const bitrateSelect = document.getElementById('audio-bitrate-select');
const playBtn = document.getElementById('audio-play-btn');
const exportBtn = document.getElementById('audio-export-btn');
const fadeInInput = document.getElementById('audio-fade-in');
const fadeOutInput = document.getElementById('audio-fade-out');
const statusEl = document.getElementById('audio-trimmer-status');
const isAr = document.documentElement.lang === 'ar';

let audioContext = null;
let decodedAudioBuffer = null;
let currentSourceNode = null;
let isPlaying = false;
let currentFile = null;
let currentProgressVal = 0;
let playbackAudioStartTime = 0;
let playbackStartSec = 0;
let playbackEndSec = 0;
let playheadRafId = null;

function resetDropzoneState() {
  currentProgressVal = 0;
  if (dropzoneDefault) dropzoneDefault.style.display = 'flex';
  if (dropzoneLoading) dropzoneLoading.style.display = 'none';
  if (dropzoneError) dropzoneError.style.display = 'none';
  if (progressBar) progressBar.style.width = '0%';
  if (loadingPct) loadingPct.textContent = '0%';
}

function showDropzoneLoading(fileName, fileSize) {
  if (dropzoneDefault) dropzoneDefault.style.display = 'none';
  if (dropzoneError) dropzoneError.style.display = 'none';
  if (dropzoneLoading) dropzoneLoading.style.display = 'flex';

  const sizeMb = (fileSize / (1024 * 1024)).toFixed(1);
  if (loadingFilename) {
    loadingFilename.textContent = `${fileName} · ${sizeMb} MB`;
  }
  setDropzoneProgress(5, isAr ? 'جارٍ قراءة ملف الصوت من جهازك...' : 'Reading audio file from storage...');
}

function setDropzoneProgress(pct, statusText) {
  currentProgressVal = Math.max(0, Math.min(100, Math.round(pct)));
  if (progressBar) {
    progressBar.style.width = `${currentProgressVal}%`;
  }
  if (loadingPct) {
    loadingPct.textContent = `${currentProgressVal}%`;
  }
  if (loadingStep && statusText) {
    loadingStep.textContent = statusText;
  }
}

function showDropzoneError(msg) {
  if (dropzoneDefault) dropzoneDefault.style.display = 'none';
  if (dropzoneLoading) dropzoneLoading.style.display = 'none';
  if (dropzoneError) dropzoneError.style.display = 'flex';
  if (errorMsg) errorMsg.textContent = msg;
}

function readFileWithProgress(file, onProgress) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        const pct = Math.round((e.loaded / e.total) * 100);
        onProgress(pct, e.loaded, e.total);
      }
    };
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('File read failed'));
    reader.readAsArrayBuffer(file);
  });
}

async function initAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }
}

function formatSec(sec) {
  const s = Math.max(0, Number(sec) || 0);
  return `${s.toFixed(1)}${isAr ? ' ث' : 's'}`;
}

async function handleFile(file) {
  if (!file) return;
  currentFile = file;

  if (file.size >= 40 * 1024 * 1024) {
    showFileAdvisory(
      dropzone.parentElement,
      isAr 
        ? `ملف صوتي كبير (${formatBytes(file.size)}) — تجري القراءة والمعالجة محلياً داخل جهازك.`
        : `Large audio file (${formatBytes(file.size)}) — processing 100% locally on your device.`
    );
  } else {
    showFileAdvisory(dropzone.parentElement, '', false);
  }

  showDropzoneLoading(file.name, file.size);
  await initAudioContext();
  trackEvent('tool_started', 'audio-trimmer');

  let progressInterval = null;

  try {
    // Stage 1: Read ArrayBuffer with real-time byte progress (5% -> 50%)
    const arrayBuffer = await readFileWithProgress(file, (pct, loaded, total) => {
      const scaledPct = Math.round(5 + (pct * 0.45));
      const loadedMb = (loaded / (1024 * 1024)).toFixed(1);
      const totalMb = (total / (1024 * 1024)).toFixed(1);
      const text = isAr 
        ? `جارٍ قراءة بيانات الصوت (${loadedMb} من ${totalMb} ميجابايت)...`
        : `Reading audio data (${loadedMb} of ${totalMb} MB)...`;
      setDropzoneProgress(scaledPct, text);
    });

    // Stage 2: Decode audio with Web Audio API (50% -> 90%)
    setDropzoneProgress(55, isAr ? 'جارٍ فك ترميز الصوت وتحليل الموجات...' : 'Decoding audio waveform & frequency data...');

    progressInterval = setInterval(() => {
      if (currentProgressVal < 88) {
        setDropzoneProgress(
          currentProgressVal + 3,
          isAr ? 'جارٍ تحليل عينات الصوت في المتصفح...' : 'Analyzing audio samples in browser...'
        );
      }
    }, 120);

    decodedAudioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    if (progressInterval) clearInterval(progressInterval);

    // Stage 3: Ready (100%)
    setDropzoneProgress(100, isAr ? 'جاهز! جارٍ فتح استوديو الصوت...' : 'Ready! Launching waveform studio...');

    await new Promise((r) => setTimeout(r, 180));

    controls.style.display = 'block';
    dropzone.style.display = 'none';
    resetDropzoneState();

    const dur = decodedAudioBuffer.duration;
    setTextSafe(totalDurationEl, `${dur.toFixed(1)} ${isAr ? 'ث' : 's'}`);
    setTextSafe(sampleRateEl, `${(decodedAudioBuffer.sampleRate / 1000).toFixed(1)} kHz`);

    startTimeInput.value = '0.0';
    startTimeInput.max = dur.toFixed(1);

    const initialEnd = Math.min(dur, Math.max(dur * 0.4, 5.0));
    endTimeInput.value = initialEnd.toFixed(1);
    endTimeInput.max = dur.toFixed(1);

    updateSelectedDuration();
    syncMarkersFromInputs();
    drawWaveform();
    setTextSafe(statusEl, '');
  } catch (err) {
    if (progressInterval) clearInterval(progressInterval);
    showDropzoneError(isAr ? 'تعذر فك ترميز ملف الصوت. تأكد أن الملف سليم ومدعوم.' : 'Could not decode this audio file. Please ensure it is a valid, uncorrupted audio track.');
  }
}

function updateSelectedDuration() {
  const start = parseFloat(startTimeInput.value) || 0;
  const end = parseFloat(endTimeInput.value) || 0;
  const diff = Math.max(0, end - start);
  setTextSafe(selectedDurationEl, `${diff.toFixed(1)} ${isAr ? 'ث' : 's'}`);
  if (markerStartVal) markerStartVal.textContent = formatSec(start);
  if (markerEndVal) markerEndVal.textContent = formatSec(end);
}

function syncMarkersFromInputs() {
  if (!decodedAudioBuffer) return;
  const dur = decodedAudioBuffer.duration;
  if (dur <= 0) return;

  const startSec = Math.max(0, parseFloat(startTimeInput.value) || 0);
  const endSec = Math.min(dur, parseFloat(endTimeInput.value) || dur);

  const startPct = Math.max(0, Math.min(100, (startSec / dur) * 100));
  const endPct = Math.max(0, Math.min(100, (endSec / dur) * 100));

  if (markerStart) {
    markerStart.style.left = `${startPct}%`;
    markerStart.setAttribute('aria-valuenow', startSec.toFixed(1));
    markerStart.setAttribute('aria-valuemin', '0');
    markerStart.setAttribute('aria-valuemax', endSec.toFixed(1));
  }
  if (markerEnd) {
    markerEnd.style.left = `${endPct}%`;
    markerEnd.setAttribute('aria-valuenow', endSec.toFixed(1));
    markerEnd.setAttribute('aria-valuemin', startSec.toFixed(1));
    markerEnd.setAttribute('aria-valuemax', dur.toFixed(1));
  }

  if (selectionBox) {
    selectionBox.style.left = `${startPct}%`;
    selectionBox.style.width = `${Math.max(0, endPct - startPct)}%`;
  }

  if (markerStartVal) markerStartVal.textContent = formatSec(startSec);
  if (markerEndVal) markerEndVal.textContent = formatSec(endSec);
}

function drawWaveform() {
  if (!decodedAudioBuffer || !waveformCanvas) return;

  const width = canvasWrap?.clientWidth || waveformCanvas.parentElement?.clientWidth || 700;
  const height = 124;
  waveformCanvas.width = width;
  waveformCanvas.height = height;

  const ctx = waveformCanvas.getContext('2d');
  ctx.clearRect(0, 0, width, height);

  const totalDur = decodedAudioBuffer.duration;
  const startSec = Math.max(0, parseFloat(startTimeInput.value) || 0);
  const endSec = Math.min(totalDur, parseFloat(endTimeInput.value) || totalDur);

  const startX = Math.round((startSec / totalDur) * width);
  const endX = Math.round((endSec / totalDur) * width);

  const rawData = decodedAudioBuffer.getChannelData(0);
  const totalSamples = rawData.length;
  const step = Math.ceil(totalSamples / width);
  const amp = (height - 8) / 2;
  const centerY = height / 2;

  const isLight = document.documentElement.getAttribute('data-theme') === 'light';

  // 1. Draw unselected background waveform bars
  const unselectedColor = isLight ? 'rgba(100, 116, 139, 0.4)' : 'rgba(148, 163, 184, 0.35)';
  ctx.fillStyle = unselectedColor;

  for (let i = 0; i < width; i++) {
    if (i >= startX && i <= endX) continue;
    let min = 1.0;
    let max = -1.0;
    for (let j = 0; j < step; j++) {
      const datum = rawData[(i * step) + j];
      if (datum < min) min = datum;
      if (datum > max) max = datum;
    }
    const barHeight = Math.max(2, (max - min) * amp);
    ctx.fillRect(i, centerY - (barHeight / 2), 1, barHeight);
  }

  // 2. Draw selected waveform bars with high contrast sapphire blue gradient
  const grad = ctx.createLinearGradient(0, 0, 0, height);
  grad.addColorStop(0, '#60a5fa');
  grad.addColorStop(0.5, '#2563eb');
  grad.addColorStop(1, '#1d4ed8');
  ctx.fillStyle = grad;

  for (let i = startX; i <= endX; i++) {
    let min = 1.0;
    let max = -1.0;
    for (let j = 0; j < step; j++) {
      const datum = rawData[(i * step) + j];
      if (datum < min) min = datum;
      if (datum > max) max = datum;
    }
    const barHeight = Math.max(3, (max - min) * amp);
    ctx.fillRect(i, centerY - (barHeight / 2), 1, barHeight);
  }

  syncMarkersFromInputs();
}

function updateExportButtonText() {
  const format = exportFormatSelect?.value?.toUpperCase() || 'MP3';
  if (exportBtn) {
    exportBtn.textContent = isAr ? `قص وتنزيل المقطع (${format})` : `Trim & Export (${format})`;
  }
  if (bitrateLabel) {
    bitrateLabel.style.display = exportFormatSelect?.value === 'mp3' ? 'flex' : 'none';
  }
}

exportFormatSelect?.addEventListener('change', updateExportButtonText);

function stopPlayback() {
  if (currentSourceNode) {
    try { currentSourceNode.stop(); } catch {}
    currentSourceNode = null;
  }
  isPlaying = false;
  if (playheadRafId) {
    cancelAnimationFrame(playheadRafId);
    playheadRafId = null;
  }
  if (playheadEl) {
    playheadEl.style.display = 'none';
  }
  if (currentTimeEl) {
    setTextSafe(currentTimeEl, '--');
  }
  if (playBtn) {
    playBtn.textContent = isAr ? 'تشغيل المقطع المحدد ▶' : 'Preview Selection ▶';
  }
}

function togglePlayback() {
  if (!decodedAudioBuffer) return;

  if (isPlaying) {
    stopPlayback();
    return;
  }

  const startSec = Math.max(0, parseFloat(startTimeInput.value) || 0);
  const endSec = Math.min(decodedAudioBuffer.duration, parseFloat(endTimeInput.value) || decodedAudioBuffer.duration);
  const duration = Math.max(0.1, endSec - startSec);
  const fadeIn = Math.min(duration / 2, Math.max(0, Number(fadeInInput.value) || 0));
  const fadeOut = Math.min(duration - fadeIn, Math.max(0, Number(fadeOutInput.value) || 0));

  currentSourceNode = audioContext.createBufferSource();
  currentSourceNode.buffer = decodedAudioBuffer;
  const gain = audioContext.createGain();
  const now = audioContext.currentTime;
  gain.gain.setValueAtTime(fadeIn > 0 ? 0.0001 : 1, now);
  if (fadeIn > 0) gain.gain.linearRampToValueAtTime(1, now + fadeIn);
  if (fadeOut > 0) {
    gain.gain.setValueAtTime(1, now + duration - fadeOut);
    gain.gain.linearRampToValueAtTime(0.0001, now + duration);
  }
  currentSourceNode.connect(gain);
  gain.connect(audioContext.destination);

  playbackAudioStartTime = now;
  playbackStartSec = startSec;
  playbackEndSec = endSec;

  const totalDur = decodedAudioBuffer.duration;
  const initialPct = (startSec / totalDur) * 100;

  if (playheadEl) {
    playheadEl.style.left = `${initialPct}%`;
    playheadEl.style.display = 'block';
  }
  const initialTimeStr = formatSec(startSec);
  if (playheadTimeEl) {
    playheadTimeEl.textContent = initialTimeStr;
  }
  if (currentTimeEl) {
    setTextSafe(currentTimeEl, initialTimeStr);
  }

  currentSourceNode.onended = () => {
    stopPlayback();
  };

  currentSourceNode.start(0, startSec, duration);
  isPlaying = true;
  playBtn.textContent = isAr ? 'إيقاف المعاينة ⏹' : 'Stop ⏹';

  function updatePlayhead() {
    if (!isPlaying || !audioContext || !decodedAudioBuffer) return;

    const elapsed = audioContext.currentTime - playbackAudioStartTime;
    const currentSec = playbackStartSec + elapsed;

    if (currentSec >= playbackEndSec) {
      stopPlayback();
      return;
    }

    const pct = Math.max(0, Math.min(100, (currentSec / totalDur) * 100));

    if (playheadEl) {
      playheadEl.style.left = `${pct}%`;
    }
    const liveTimeStr = formatSec(currentSec);
    if (playheadTimeEl) {
      playheadTimeEl.textContent = liveTimeStr;
    }
    if (currentTimeEl) {
      setTextSafe(currentTimeEl, liveTimeStr);
    }
    if (playheadBadge) {
      if (pct < 7) {
        playheadBadge.style.transform = 'translateX(0)';
      } else if (pct > 93) {
        playheadBadge.style.transform = 'translateX(-100%)';
      } else {
        playheadBadge.style.transform = 'translateX(-50%)';
      }
    }

    playheadRafId = requestAnimationFrame(updatePlayhead);
  }

  playheadRafId = requestAnimationFrame(updatePlayhead);
}

function getTrimmedPCMData(startSec, endSec) {
  const sampleRate = decodedAudioBuffer.sampleRate;
  const numChannels = decodedAudioBuffer.numberOfChannels;
  const startSample = Math.floor(startSec * sampleRate);
  const endSample = Math.floor(endSec * sampleRate);
  const length = endSample - startSample;

  const duration = length / sampleRate;
  const fadeInSamples = Math.floor(Math.min(duration / 2, Math.max(0, Number(fadeInInput.value) || 0)) * sampleRate);
  const fadeOutSamples = Math.floor(Math.min(duration - fadeInSamples / sampleRate, Math.max(0, Number(fadeOutInput.value) || 0)) * sampleRate);

  const channels = [];
  for (let ch = 0; ch < numChannels; ch++) {
    const src = decodedAudioBuffer.getChannelData(ch).subarray(startSample, endSample);
    const dst = new Float32Array(length);
    for (let i = 0; i < length; i++) {
      let sample = Math.max(-1, Math.min(1, src[i]));
      const inGain = fadeInSamples ? Math.min(1, i / fadeInSamples) : 1;
      const outGain = fadeOutSamples ? Math.min(1, (length - 1 - i) / fadeOutSamples) : 1;
      dst[i] = sample * Math.min(inGain, outGain);
    }
    channels.push(dst);
  }
  return { channels, length, sampleRate, numChannels };
}

function createWavBlob({ channels, length, sampleRate, numChannels }) {
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = length * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  function writeString(offset, str) {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  }

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      let s = Math.max(-1, Math.min(1, channels[ch][i]));
      s = s < 0 ? s * 0x8000 : s * 0x7FFF;
      view.setInt16(offset, s, true);
      offset += 2;
    }
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

function createMp3Blob({ channels, length, sampleRate, numChannels }, kbps = 192) {
  const encoder = new Mp3Encoder(numChannels, sampleRate, kbps);
  const mp3Data = [];
  const sampleBlockSize = 1152;

  const leftInt16 = new Int16Array(length);
  for (let i = 0; i < length; i++) {
    const s = Math.max(-1, Math.min(1, channels[0][i]));
    leftInt16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }

  let rightInt16 = null;
  if (numChannels > 1) {
    rightInt16 = new Int16Array(length);
    for (let i = 0; i < length; i++) {
      const s = Math.max(-1, Math.min(1, channels[1][i]));
      rightInt16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
  }

  for (let i = 0; i < length; i += sampleBlockSize) {
    const leftChunk = leftInt16.subarray(i, i + sampleBlockSize);
    let buf;
    if (numChannels === 1) {
      buf = encoder.encodeBuffer(leftChunk);
    } else {
      const rightChunk = rightInt16.subarray(i, i + sampleBlockSize);
      buf = encoder.encodeBuffer(leftChunk, rightChunk);
    }
    if (buf.length > 0) mp3Data.push(buf);
  }

  const flushBuf = encoder.flush();
  if (flushBuf.length > 0) mp3Data.push(flushBuf);

  return new Blob(mp3Data, { type: 'audio/mp3' });
}

async function exportAudio() {
  if (!decodedAudioBuffer) return;

  const startSec = Math.max(0, parseFloat(startTimeInput.value) || 0);
  const endSec = Math.min(decodedAudioBuffer.duration, parseFloat(endTimeInput.value) || decodedAudioBuffer.duration);

  if (endSec <= startSec) {
    setTextSafe(statusEl, isAr ? 'وقت النهاية يجب أن يكون بعد وقت البداية.' : 'End time must be after start time.');
    return;
  }

  const format = exportFormatSelect?.value || 'mp3';
  const bitrate = Number(bitrateSelect?.value) || 192;
  const pcm = getTrimmedPCMData(startSec, endSec);

  if (pcm.length <= 0) {
    setTextSafe(statusEl, isAr ? 'المقطع المحدد قصير جدًا.' : 'The selected audio clip is too short.');
    return;
  }

  exportBtn.disabled = true;
  setTextSafe(statusEl, isAr ? `جارٍ معالجة وتصدير الصوت بصيغة ${format.toUpperCase()}...` : `Encoding ${format.toUpperCase()} audio...`);

  try {
    let blob;
    let ext = format;

    if (format === 'mp3') {
      blob = createMp3Blob(pcm, bitrate);
      ext = 'mp3';
    } else if (format === 'wav') {
      blob = createWavBlob(pcm);
      ext = 'wav';
    } else if (format === 'webm' || format === 'aac') {
      // Use Web Audio Offline buffer or MediaRecorder if supported, fallback to MP3
      const mime = format === 'aac' && MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm;codecs=opus';
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(mime)) {
        const streamDest = audioContext.createMediaStreamDestination();
        const bufferSource = audioContext.createBufferSource();
        const trimBuffer = audioContext.createBuffer(pcm.numChannels, pcm.length, pcm.sampleRate);
        for (let ch = 0; ch < pcm.numChannels; ch++) {
          trimBuffer.copyToChannel(pcm.channels[ch], ch);
        }
        bufferSource.buffer = trimBuffer;
        bufferSource.connect(streamDest);

        const chunks = [];
        const recorder = new MediaRecorder(streamDest.stream, { mimeType: mime });
        recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
        const recordPromise = new Promise((resolve) => { recorder.onstop = resolve; });
        recorder.start();
        bufferSource.start();

        await new Promise((r) => setTimeout(r, Math.min(2500, (pcm.length / pcm.sampleRate) * 1000 + 100)));
        recorder.stop();
        bufferSource.stop();
        await recordPromise;
        blob = new Blob(chunks, { type: mime });
        ext = format === 'aac' ? 'm4a' : 'webm';
      } else {
        blob = createMp3Blob(pcm, bitrate);
        ext = 'mp3';
      }
    }

    const downloadName = `${baseName}-trimmed.${ext}`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 12000);

    const sizeKb = (blob.size / 1024).toFixed(0);
    setTextSafe(statusEl, isAr ? `تم تنزيل ${downloadName} (${sizeKb} كيلوبايت)` : `Downloaded ${downloadName} (${sizeKb} KB)`);
    soundEngine.playSuccess();
    trackEvent('tool_completed', 'audio-trimmer', { format: ext, duration: (endSec - startSec).toFixed(1) });
  } catch (err) {
    setTextSafe(statusEl, isAr ? 'حدث خطأ أثناء تصدير الصوت.' : 'An error occurred during audio export.');
  } finally {
    exportBtn.disabled = false;
  }
}

// Interactive DAW Range Handle Dragging
let activeDrag = null;
let isRecentDrag = false;

function setupDrag(element, type) {
  if (!element) return;

  const onMove = (e) => {
    if (!activeDrag || activeDrag.pointerId !== e.pointerId || !decodedAudioBuffer) return;
    const rect = canvasWrap.getBoundingClientRect();
    if (!rect.width) return;
    const dur = decodedAudioBuffer.duration;
    const deltaX = e.clientX - activeDrag.startX;
    const deltaSec = (deltaX / rect.width) * dur;

    if (Math.abs(deltaX) > 2) {
      activeDrag.hasMoved = true;
    }

    if (activeDrag.type === 'start') {
      const newStart = Math.max(0, Math.min(activeDrag.initialEnd - 0.2, activeDrag.initialStart + deltaSec));
      startTimeInput.value = newStart.toFixed(1);
    } else if (activeDrag.type === 'end') {
      const newEnd = Math.min(dur, Math.max(activeDrag.initialStart + 0.2, activeDrag.initialEnd + deltaSec));
      endTimeInput.value = newEnd.toFixed(1);
    } else if (activeDrag.type === 'selection') {
      const windowDur = activeDrag.initialEnd - activeDrag.initialStart;
      let newStart = activeDrag.initialStart + deltaSec;
      let newEnd = activeDrag.initialEnd + deltaSec;
      if (newStart < 0) {
        newStart = 0;
        newEnd = windowDur;
      } else if (newEnd > dur) {
        newEnd = dur;
        newStart = Math.max(0, dur - windowDur);
      }
      startTimeInput.value = newStart.toFixed(1);
      endTimeInput.value = newEnd.toFixed(1);
    }

    updateSelectedDuration();
    drawWaveform();
  };

  const onUp = (e) => {
    if (activeDrag && activeDrag.pointerId === e.pointerId) {
      if (activeDrag.hasMoved) {
        isRecentDrag = true;
        setTimeout(() => { isRecentDrag = false; }, 80);
      }
      try {
        if (element.hasPointerCapture && element.hasPointerCapture(e.pointerId)) {
          element.releasePointerCapture(e.pointerId);
        }
      } catch {}
      element.classList.remove('is-dragging');
      if (selectionBox) selectionBox.classList.remove('is-dragging');
      activeDrag = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    }
  };

  element.addEventListener('pointerdown', (e) => {
    if (!decodedAudioBuffer) return;
    if (isPlaying) stopPlayback();
    e.preventDefault();
    e.stopPropagation();

    try {
      element.setPointerCapture(e.pointerId);
    } catch {}

    activeDrag = {
      type,
      pointerId: e.pointerId,
      startX: e.clientX,
      hasMoved: false,
      initialStart: parseFloat(startTimeInput.value) || 0,
      initialEnd: parseFloat(endTimeInput.value) || 0
    };
    element.classList.add('is-dragging');
    if (selectionBox) selectionBox.classList.add('is-dragging');

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  });
}

setupDrag(markerStart, 'start');
setupDrag(markerEnd, 'end');
setupDrag(handleL, 'start');
setupDrag(handleR, 'end');
setupDrag(selectionBox, 'selection');

function jumpToTimelineClick(clientX, boundingEl) {
  if (!decodedAudioBuffer || activeDrag || isRecentDrag) return;
  if (isPlaying) stopPlayback();
  const rect = boundingEl.getBoundingClientRect();
  if (!rect.width) return;
  const clickX = clientX - rect.left;
  const clickedSec = Math.max(0, Math.min(decodedAudioBuffer.duration, (clickX / rect.width) * decodedAudioBuffer.duration));

  const start = parseFloat(startTimeInput.value) || 0;
  const end = parseFloat(endTimeInput.value) || 0;

  if (Math.abs(clickedSec - start) < Math.abs(clickedSec - end)) {
    startTimeInput.value = Math.min(clickedSec, end - 0.2).toFixed(1);
  } else {
    endTimeInput.value = Math.max(clickedSec, start + 0.2).toFixed(1);
  }
  updateSelectedDuration();
  drawWaveform();
}

if (canvasWrap) {
  canvasWrap.addEventListener('click', (e) => {
    if (e.target.closest('#audio-selection-box') || e.target.closest('.audio-marker')) return;
    jumpToTimelineClick(e.clientX, canvasWrap);
  });
}

const rulerTrack = document.getElementById('audio-ruler-track');
if (rulerTrack) {
  rulerTrack.addEventListener('click', (e) => {
    if (e.target.closest('.audio-marker')) return;
    jumpToTimelineClick(e.clientX, rulerTrack);
  });
}

function setupMarkerKeyboard(marker, isStart) {
  if (!marker) return;
  marker.addEventListener('keydown', (e) => {
    if (!decodedAudioBuffer) return;
    let step = 0;
    if (e.key === 'ArrowLeft') step = -0.1;
    else if (e.key === 'ArrowRight') step = 0.1;
    else if (e.key === 'PageDown') step = -1.0;
    else if (e.key === 'PageUp') step = 1.0;
    if (step === 0) return;
    e.preventDefault();

    const dur = decodedAudioBuffer.duration;
    if (isStart) {
      const cur = parseFloat(startTimeInput.value) || 0;
      const end = parseFloat(endTimeInput.value) || dur;
      const next = Math.max(0, Math.min(end - 0.2, cur + step));
      startTimeInput.value = next.toFixed(1);
    } else {
      const cur = parseFloat(endTimeInput.value) || dur;
      const start = parseFloat(startTimeInput.value) || 0;
      const next = Math.min(dur, Math.max(start + 0.2, cur + step));
      endTimeInput.value = next.toFixed(1);
    }
    updateSelectedDuration();
    drawWaveform();
  });
}

setupMarkerKeyboard(markerStart, true);
setupMarkerKeyboard(markerEnd, false);

startTimeInput?.addEventListener('input', () => {
  updateSelectedDuration();
  drawWaveform();
});
endTimeInput?.addEventListener('input', () => {
  updateSelectedDuration();
  drawWaveform();
});

playBtn?.addEventListener('click', togglePlayback);
exportBtn?.addEventListener('click', exportAudio);

window.addEventListener('resize', () => {
  if (decodedAudioBuffer) drawWaveform();
});

if (dropzone && fileInput) {
  dropzone.addEventListener('click', (e) => {
    if (e.target.closest('#trimmer-retry-btn') || e.target.closest('#trimmer-dropzone-loading')) return;
    fileInput.click();
  });
  dropzone.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (event.target.closest('#trimmer-retry-btn')) return;
      event.preventDefault();
      fileInput.click();
    }
  });
  fileInput.addEventListener('change', (e) => {
    if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]);
  });
  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('dragover');
  });
  dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  });
}

retryBtn?.addEventListener('click', (e) => {
  e.stopPropagation();
  resetDropzoneState();
  if (fileInput) {
    fileInput.value = '';
    fileInput.click();
  }
});

changeFileBtn?.addEventListener('click', () => {
  stopPlayback();
  if (controls) controls.style.display = 'none';
  if (dropzone) dropzone.style.display = 'flex';
  resetDropzoneState();
  if (fileInput) {
    fileInput.value = '';
    fileInput.click();
  }
});
